import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Stack,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { salesPerson } from "@app/modules/salesperson/slice";
import { useDispatch } from "react-redux";
import { useRequestState } from "@app/hooks";
import { selectSalesPersonState } from "@app/modules/salesperson/selector";
import { AssignAgent, AssignSalesPerson } from "../slice";
import { useLocation, useNavigate } from "react-router-dom";

// ✅ Define a TypeScript interface for salesperson
interface Salesperson {
  id: string;
  name: string;
  phone: string;
  email: string;
}

interface AssignAgentModalComponentProps {
  open: boolean;
  onClose: () => void;
}

const AssignAgentsModal: React.FC<
  AssignAgentModalComponentProps
> = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate(); 
  const theme = useTheme();
  const { requestId } = location.state as { requestId: number };

  const dispatch = useDispatch();

  const fontFamily = "'Work Sans', sans-serif";

  const { data } = useRequestState({
    stateSelector: selectSalesPersonState,
  }) as { data: Salesperson[] };

  const filteredSalespeople: Salesperson[] = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return data || [];

    return (data || []).filter(
      (person: Salesperson) =>
        person.name.toLowerCase().includes(query) ||
        person.phone.toLowerCase().includes(query) ||
        person.email.toLowerCase().includes(query)
    );
  }, [searchQuery, data]);

  const handleToggle = (id: string) => {
    setSelected((prev) => {
      const currentIndex = prev.indexOf(id);
      const newSelected = [...prev];
      if (currentIndex === -1) newSelected.push(id);
      else newSelected.splice(currentIndex, 1);
      return newSelected;
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    dispatch(salesPerson({ role: "Agent" }));
  }, [dispatch]);

  const handleSubmit = (data: any) => {
    dispatch(
      AssignAgent({
        id: requestId,
        salesPersonId: data.id,
      })
    );

    navigate(-1);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          width: 520,
          borderRadius: 3,
        },
      }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle
            sx={{
              p: 0,
              fontWeight: 500,
              fontSize: "1.25rem",
              fontFamily,
              color: "#000000",
            }}
          >
            Assign Agent
          </DialogTitle>
          <IconButton onClick={handleClose}>
            <CloseIcon htmlColor="#080808" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ py: 0, px: 3 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: "#F7F9FB",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E0E0E0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#BDBDBD",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          size="small"
          sx={{ my: 2 }}
        />

        {/* Salesperson List */}
        <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1 }}>
          <List disablePadding>
            {filteredSalespeople.map((person: Salesperson) => {
              const labelId = `checkbox-list-label-${person.id}`;
              const isSelected = selected.includes(person.id);

              return (
                <ListItem
                  key={person.id}
                  onClick={() => handleToggle(person.id)}
                  dense
                  disablePadding
                  sx={{
                    mb: 1.5,
                    cursor: "pointer",
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    p: 1.5,
                    "&:hover": { backgroundColor: "#F7F9FB" },
                    ...(isSelected && {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: "#F0F7FF",
                    }),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 1.5 }}>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <Stack spacing={0}>
                    <Typography
                      id={labelId}
                      variant="body1"
                      fontWeight={500}
                      fontFamily={fontFamily}
                      color="text.primary"
                    >
                      {person.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily={fontFamily}
                      color="text.secondary"
                    >
                      {person.phone}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily={fontFamily}
                      color="text.secondary"
                    >
                      {person.email}
                    </Typography>
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            flex: 1,
            fontWeight: 700,
            fontSize: "16px",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            py: 1,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#F0F7FF",
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          Close
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            flex: 1,
            textTransform: "none",
            borderRadius: "8px",
            px: 4,
            fontWeight: 700,
            fontSize: "16px",
            backgroundColor: theme.palette.primary.main,
            py: 1,
            color: "white",
            "&:hover": {
              backgroundColor: "#115FAB",
            },
          }}
          disabled={loading || selected.length === 0}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            "Send"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AssignAgentModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <AssignAgentsModal open={isOpen} onClose={handleClose} />
  );
};

export default AssignAgentModal;
